import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import FirebaseAdapter from "@wc/adapters/firebase";

/**
 * @param {object} props Component props
 * @param {boolean} props.isOpen Whether the modal is open
 * @param {(isOpen: boolean) => void} props.onOpenChange Callback when modal open state changes
 * @param {() => void} [props.onSuccess] Callback after successful creation
 * @returns {React.ReactElement} The create organization modal
 */
export default function CreateOrgModal({ isOpen, onOpenChange, onSuccess }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orgName, setOrgName] = React.useState("");
  const [isCreating, setIsCreating] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleCreate = async () => {
    if (!orgName.trim()) {
      setError("Organization name is required");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      // Create slug from name
      const slug = orgName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      // Call the function - it creates org, adds user as admin, creates default team, sets claims
      const result = await FirebaseAdapter().functions.createOrganization({
        displayName: orgName.trim(),
        name: slug,
      });

      // Refresh token to get new claims
      const currentUser = FirebaseAdapter().authn.getCurrentUser();
      if (currentUser) {
        await FirebaseAdapter().authn.refreshUserToken(currentUser);
        // Wait a bit for the auth state to propagate
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      setOrgName("");
      onOpenChange(false);

      if (onSuccess) {
        onSuccess();
      }

      // Navigate to the new org using its ID
      navigate(`/orgs/${result.organizationId}/overview`);
    } catch (err) {
      console.error("Failed to create organization:", err);
      setError(
        err.message === "ORG_CREATION_LIMIT_REACHED"
          ? "You have reached the maximum number of organizations"
          : "Failed to create organization. Please try again.",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setOrgName("");
    setError(null);
    onOpenChange(false);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} onClose={handleClose}>
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          {t("org.createOrg")}
        </ModalHeader>
        <ModalBody>
          <Input
            autoFocus
            label={t("org.orgNameLabel")}
            placeholder={t("org.orgNamePlaceholder")}
            value={orgName}
            onValueChange={setOrgName}
            isDisabled={isCreating}
            isInvalid={!!error}
            errorMessage={error}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isCreating) {
                handleCreate();
              }
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={handleClose} isDisabled={isCreating}>
            {t("cancel")}
          </Button>
          <Button
            color="primary"
            onPress={handleCreate}
            isLoading={isCreating}
            isDisabled={isCreating || !orgName.trim()}
          >
            {isCreating ? t("org.creating") : t("org.createOrg")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
